/**
 * 備考 (note) の文字列を「プレーンテキスト」と「URL」の列に分解する。
 *
 * 自動リンク化するのは `http://` と `https://` だけ。`javascript:` や `data:` など他のスキームは
 * URL と見なさず、プレーンテキストのまま返す (表示側は文字列として描画するので HTML として解釈されない)。
 *
 * URL の範囲:
 *   - ASCII の URL で使える文字 (RFC 3986 の予約文字 + 非予約文字 + `%`) だけ。空白 (改行含む)、
 *     `< > " ' \`` 、日本語などの非 ASCII 文字で終わる。日本語の文章は空白で区切られないため、
 *     「https://example.com/a、次に…」の「、次に…」を URL に巻き込まないようにする。
 *     非 ASCII を含む URL は、パーセントエンコードして貼る。
 *   - 末尾の句読点 (`.,;:!?` と「、。」等) と、対にならない閉じ括弧は URL に含めない
 *     (「(https://example.com/a)」の括弧をリンクに含めないため)。
 *   - ホスト名の無い `https://` や `https:///path` は URL にしない (`new URL` は後者を
 *     `https://path/` へ正規化してしまい、見た目と遷移先が食い違う)。
 *   - パースできないもの・資格情報 (`user:pass@`) 付きのものは URL にしない
 *     (見た目と遷移先が食い違うフィッシングを避ける)。
 */

/** 分解結果の 1 要素 */
export type LinkifiedPart =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string };

/** URL 候補。開始は `http(s)://` のみ (大文字小文字は区別しない)。 */
const URL_CANDIDATE = /https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&()*+,;=%]+/giu;

/** URL の末尾に置かれても URL の一部としない句読点 (候補は ASCII だけなので ASCII のみ) */
const TRAILING_PUNCTUATION = /[.,;:!?*~]$/u;

/** 閉じ括弧と、対応する開き括弧 */
const CLOSING_BRACKETS: Readonly<Record<string, string>> = {
  ')': '(',
  ']': '[',
  '}': '{',
};

function count(text: string, char: string): number {
  return text.split(char).length - 1;
}

/** 候補の末尾から、URL に含めない文字を取り除く。 */
function trimTrailing(candidate: string): string {
  let url = candidate;
  for (;;) {
    if (TRAILING_PUNCTUATION.test(url)) {
      url = url.slice(0, -1);
      continue;
    }
    const last = url.slice(-1);
    const opening = CLOSING_BRACKETS[last];
    // 対になる開き括弧が URL の中に無い閉じ括弧だけを外す (Wikipedia の `/Foo_(bar)` は残す)。
    if (opening !== undefined && count(url, last) > count(url, opening)) {
      url = url.slice(0, -1);
      continue;
    }
    return url;
  }
}

/** リンクにしてよい URL か (パースでき、http(s) で、ホスト名があり、資格情報を含まない)。 */
function toSafeHref(url: string): string | null {
  // スキーム直後が `/` や `\` のときはホストが無い (ブラウザは無視して次の要素をホストにする)。
  if (/^https?:\/\/[/\\]/iu.test(url)) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (parsed.hostname === '') return null;
  if (parsed.username !== '' || parsed.password !== '') return null;
  return parsed.href;
}

/**
 * 文字列をプレーンテキストと URL の列に分解する。連結すると元の文字列に戻る。
 *
 * `href` は `new URL()` で正規化した値。表示用の `text` は元の文字列のまま。
 */
export function linkify(value: string): LinkifiedPart[] {
  const parts: LinkifiedPart[] = [];
  let cursor = 0;
  const pushText = (text: string): void => {
    if (text === '') return;
    const last = parts[parts.length - 1];
    // 連続するプレーンテキストは 1 つにまとめる (URL ではなかった候補を戻すときを含む)。
    if (last?.type === 'text') last.text += text;
    else parts.push({ type: 'text', text });
  };

  for (const match of value.matchAll(URL_CANDIDATE)) {
    const start = match.index ?? 0;
    if (start < cursor) continue;
    const url = trimTrailing(match[0]);
    const href = toSafeHref(url);
    pushText(value.slice(cursor, start));
    if (href === null) {
      // リンクにできない候補は、末尾の句読点も含めて元の文字列のまま出す。
      pushText(match[0]);
    } else {
      parts.push({ type: 'link', text: url, href });
      pushText(match[0].slice(url.length));
    }
    cursor = start + match[0].length;
  }
  pushText(value.slice(cursor));
  return parts;
}
